import { AgentConfig, GenerateSystemPromptRequest, PromptConfig, ToolConfig } from "build-ts/agent_config";
import { Header } from "build-ts/metadata";
import { AnalysisTreeProto } from "build-ts/analysis";
import { StatefulResponse, AnalysisTreeRequestType } from "build-ts/stateful_api";
import { StatefulRequestBuilder } from "containers/stateful/StatefulRequests";
import { statefulAnalysisTree } from "./AnalysisTreeServices";
import { v4 as uuidv4 } from "uuid";
import showToast from "hooks/useCustomToast";

export const getAgentsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_AGENT_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch agents list: ${errorMessage}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching agents list:", error);
        showToast("Failed to fetch agents list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const getToolsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_TOOL_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch tools list: ${errorMessage}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching tools list:", error);
        showToast("Failed to fetch tools list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const getAgentById = async (agentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_AGENT}/${agentId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch agent data: ${errorMessage}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching agent data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch agent data", errorMessage, "error");
        return null;
    }
};

export const createNewAgent = async (agentData: AgentConfig) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_AGENT}`, {
            method: "POST",
            headers: {
            "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(agentData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to create agent: ${errorMessage}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error creating agent:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to create agent", errorMessage, "error");
        return null;
    }
};

export const updateAgent = async (agent_id: string | undefined, agentConfig: AgentConfig, selectedTools: ToolConfig[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_AGENT}/${agent_id}`, {
            method: "PUT",
            headers: {
            "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
            name: agentConfig.name,
            description: agentConfig.description,
            is_active: agentConfig.is_active,
            analysis_flag: agentConfig.analysis_flag,
            version: agentConfig.version,
            tools: selectedTools.map(tool => ({ id: tool.id })),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to update agent: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Agent updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating agent:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update agent", errorMessage, "error");
        return null;
    }
};

export const updateTool = async (toolId: string | undefined, toolData: ToolConfig) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_TOOL}/${toolId}`, {
            method: "PUT",
            headers: {
            "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                name_in_agent: toolData.name_in_agent,
                description: toolData.description,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to update tool: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Tool updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating tool:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update tool", errorMessage, "error");
        return null;
    }
};

export const duplicateAgents = async (agentIds: string[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_EVA_AGENT_DUPLICATE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ agentIds }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to duplicate agents: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Agent(s) duplicated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error duplicating agents:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to duplicate agents", errorMessage, "error");
        return null;
    }
};

export const getSystemPromptDocumentation = async () => {
    try{
        const response = await fetch(`${process.env.REACT_APP_EVA_DOC}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch documentation: ${errorMessage}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching documentation:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to fetch documentation", errorMessage, "error");
        return null;
    }
};

// Generate system prompt
export const generatePrompt = async (request: GenerateSystemPromptRequest) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_GENERATE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to generate prompt: ${response.statusText}`);
    }

    return await response.json();
};

// prompt list api
export const getAssignedPrompts = async (agentId: string) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_AGENT}/${agentId}/prompts/list`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch assigned prompts: ${response.statusText}`);
    }

    return await response.json();
};

// Get Prompt Detail by Id
export const getPromptByid = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_PROMPT}/${promptId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Update a Prompt
export const updatePrompt = async (promptId: string, promptData: PromptConfig) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_PROMPT}/${promptId}`, {
        method: "PUT",
        headers: {
        "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
            name: promptData.name,
            philosophy: promptData.philosophy,
            instructions: promptData.instructions,
            is_active: promptData.is_active,
            model_name: promptData.model_name,
            model_type: promptData.model_type,
            temperature: promptData.temperature,
        }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Duplicate a Prompt
export const duplicatePrompt = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_PROMPT}/${promptId}/duplicate`, {
        method: "POST",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to duplicate the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Delete a Prompt
export const deletePrompt = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_EVA_PROMPT}/${promptId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete the prompt: ${response.statusText}`);
    }

    return true;
};

// Newton API calls
export const newtonApi = {
    // Fetch available tree headers
    async getTrees(pageSize: number = 100, pageNumber: number = 1): Promise<{ trees: Header[]; total: number }> {
      const response = await fetch(
        `${process.env.REACT_APP_GET_ALL_TREE_HEADERS}?pageSize=${pageSize}&pageNumber=${pageNumber}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch trees: ${response.statusText}`);
      }

      const data = await response.json();
      // The API returns an array of Header objects directly
      return {
        trees: Array.isArray(data) ? data : [],
        total: Array.isArray(data) ? data.length : 0,
      };
    },

    // Fetch complete tree data by ID using stateful API
    async getTreeById(treeId: string): Promise<AnalysisTreeProto> {
      const transactionId = uuidv4();
      const requestBuilder = new StatefulRequestBuilder(transactionId);
      const loadTreeRequest = requestBuilder.LoadTreeRequest(treeId);

      try {
        const response: StatefulResponse = await statefulAnalysisTree(loadTreeRequest);
        if (response) {
          const tree = response.analysis_tree_responses
            ?.find((response) => response.type === AnalysisTreeRequestType.GET_TREE)
            ?.get_tree?.tree;

          if (tree) {
            return tree;
          } else {
            throw new Error("Tree not found in response");
          }
        } else {
          throw new Error("No response received");
        }
      } catch (error) {
        throw new Error(`Failed to fetch tree data: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    },
};
